import Phaser from 'phaser';
import Board, {Grid} from '../board';
import i18n from '../../assets/i18n.json';
import ModalService from '../modalService';
import { BoardElement, Mode, Winner } from '../types';
import { checkBoardAroundPoint } from '../utils/checkBoardAroundPoint';
import { findMaxElementInMatrix } from '../utils/findMaxElementInMatrix';
import { findNearestEmptyElement } from '../utils/findNearestEmptyElement';
import Sdk from '../sdk';
import debounce from '../utils/debounce';
import { Scene } from '../utils/consts';
import HomeScene from './HomeScene';
import Onboarding from '../Onboarding';

// Подразумеваем, что если мультиплеер, то играем за крестики

let scale = 1;

export default class MainScene extends Phaser.Scene {
    private board: Board;
    private gridArray: Grid;
    private modalService: ModalService;
    private BOARD_SIZE = 40;
    private mode: Mode;
    private score: number = 0;
    private resizeObserver?: ResizeObserver;
    private finish: boolean = false;
    private onboarding: Onboarding;

    private prevStep: 'cross' | 'zero' = 'zero';

    constructor(mode: Mode) {
        super(mode);
    }

    onResize() {
        if (this.game) {
            this.updatePositions();
        }
    }

    preload() {
        this.load.image('zero', 'assets/zero.png');
        this.load.image('cross', 'assets/cross.png');
        this.load.image('hand', 'assets/hand.png');
    }

    create() {
        if (this.renderer.width < 700 || this.renderer.height < 700) {
            scale = 0.5;
        } else {
            scale = 1;
        }
        this.mode = this.sys.config as 'singleplayer' | 'multiplayer';
        this.gridArray = this.initGridArray();
        this.board = new Board({ scene: this, onClick: this.onBoardClickCallback.bind(this), gridArray: this.gridArray, scale });
        this.modalService = new ModalService({ scene: this });
        this.resizeObserver = new ResizeObserver(debounce(this.onResize.bind(this), 100));
        this.resizeObserver.observe(this.game.canvas);

        const playerData = this.game.registry.get('playerData') || {};
        if (!playerData.onboarded) {
            this.onboarding = new Onboarding(this, this.board, () => {
                (this.game.registry.get('sdk') as Sdk).setPlayerData(Object.assign(playerData, { onboarded: true }));
            });
        } else {
            (this.registry.get('sdk') as Sdk).showFullscreenAdv();
        }
    }

    private onBoardClickCallback(y: number, x: number, clickedOnEmptyCell: boolean) {
        if (this.finish) {
            return;
        }

        if (clickedOnEmptyCell) {
            const newGrid = this.board.gridArray;
            newGrid[y][x] = this.prevStep === 'zero' ? 1 : 0;
            this.prevStep = this.prevStep === 'cross' ? 'zero' : 'cross';
            this.board.gridArray = newGrid;

            const { maxLength, winArray } = checkBoardAroundPoint(this.board.gridArray, { y, x });
            if (maxLength >= 5) {
                this.showFinish(this.board.gridArray[y][x] === 0 ? 'zero' : 'cross', winArray);
                return;
            }
        }

        if (this.mode === 'singleplayer' && this.prevStep == 'cross') {
            this.score += 10;
            setTimeout(() => this.doNextMove({ x, y }));
        }
    }

    private showFinish(winner: Winner, arrayToAnimate: Array<BoardElement>) {
        const texts = i18n[(this.registry.get('sdk') as Sdk).getLang()];
        this.finish = true;
        this.animateBoardItems(arrayToAnimate).then(() => {
            let text = `${winner == 'cross' ? texts.crosses : texts.zeros} ${texts.win}!`;
            let score: null | number = null;
            if (this.mode === 'singleplayer' && winner === 'cross') {
                score = this.score;
                
                const playerData = this.game.registry.get('playerData') || {};
                playerData.topScore = Math.max(Math.round(this.score), playerData.topScore || 0);
                (this.game.registry.get('sdk') as Sdk).setPlayerData(playerData);
                (this.game.registry.get('sdk') as Sdk).setLeaderboardData(playerData.topScore);
            }
            this.modalService.showFinishModal(
                text,
                winner,
                score,
                () => {
                    this.modalService.close();
                    this.board.restart();
                    this.prevStep = 'zero'
                    this.finish = false;
                    this.score = 0;
                    (this.registry.get('sdk') as Sdk).showFullscreenAdv();
                },
                () => {
                    this.game.scene.add(Scene.HOME, new HomeScene(), true);
                    this.scene.remove();
                });
        });
    }

    private async animateBoardItems(itemsToAnimate: Array<BoardElement>) {
        const imagesGrid = this.board.getImagesGrid();
        const firstIndexes = itemsToAnimate[0].position;
        const lastIndexes = itemsToAnimate[itemsToAnimate.length - 1].position;
        const x1 = imagesGrid[firstIndexes.i][firstIndexes.j]!.x;
        const y1 = imagesGrid[firstIndexes.i][firstIndexes.j]!.y;
        const x2 = imagesGrid[lastIndexes.i][lastIndexes.j]!.x;
        const y2 = imagesGrid[lastIndexes.i][lastIndexes.j]!.y;

        this.board.line = this.add.line(
            0, 0, x1, y1, x2, y2, 0x78ad40
        ).setOrigin(0).setLineWidth(5);

        return new Promise<void>(async (resolve) => {
            this.board.boardContainer.add(this.board.line!);
            await new Promise<void>(r => setTimeout(r, 1000));
            resolve();
        });
    }

    private initGridArray(): Grid {
        return new Array(this.BOARD_SIZE).fill(null).map(() => new Array(this.BOARD_SIZE).fill(null));
    }

    private doNextMove(opponentMove: { x: number, y: number }) {
        const nextMove = this.findNextMoveAroundPoint(opponentMove);
        this.onBoardClickCallback(nextMove.y, nextMove.x, true);
    }

    private findNextMoveAroundPoint(opponentMove: { x: number, y: number }): { x: number, y: number } {
        const matrixToFindSize = 10;
        const originalGridSlice = new Array(matrixToFindSize*2 + 1).fill(0).map(() => new Array(matrixToFindSize*2 + 1).fill(0));
        const finalResultMatrix = new Array(matrixToFindSize*2 + 1).fill(0).map(() => new Array(matrixToFindSize*2 + 1).fill(0));

        for (let i = opponentMove.y - matrixToFindSize; i <= opponentMove.y + matrixToFindSize; i++) {
            for (let j = opponentMove.x - matrixToFindSize; j <= opponentMove.x + matrixToFindSize; j++) {
                originalGridSlice[i+matrixToFindSize - opponentMove.y][j+matrixToFindSize-opponentMove.x] = this.board.gridArray[i]?.[j];
            }
        }

        const zerosResultMatrix = this.fillResultMatrix(originalGridSlice, 0, matrixToFindSize);
        const crossesResultMatrix = this.fillResultMatrix(originalGridSlice, 1, matrixToFindSize);

        for (let i = 0; i < matrixToFindSize*2 + 1; i++) {
            for (let j = 0; j < matrixToFindSize*2 + 1; j++) {
                // Крестики более значимы, так как их нужно перекрыть
                finalResultMatrix[i][j] = zerosResultMatrix[i][j] + crossesResultMatrix[i][j] * 2;
            }
        }

        const { maxValue, position } = findMaxElementInMatrix(finalResultMatrix);
        let nextPosition = position;
        if (maxValue === 2) {
            nextPosition = findNearestEmptyElement(zerosResultMatrix) || nextPosition;
        }

        return {
            x: opponentMove.x + nextPosition.j - matrixToFindSize,
            y: opponentMove.y + nextPosition.i - matrixToFindSize
        };
    }

    private copyBoard(board) {
        return structuredClone(board);
    }

    private fillResultMatrix(matrix, value: 0 | 1, size: number): Array<Array<number>> {
        const resultMatrix = new Array(size*2 + 1).fill(0).map(() => new Array(size*2 + 1).fill(0));

        for (let i = 0; i < size*2 + 1; i++) {
            for (let j = 0; j < size*2 + 1; j++) {
                const copiedSlice = this.copyBoard(matrix);
                if (copiedSlice[i][j] === undefined || copiedSlice[i][j] === 1 || copiedSlice[i][j] === 0) {
                    continue; // Вне поля
                }
                if (copiedSlice[i][j] === null) {
                    copiedSlice[i][j] = value;
                }
                resultMatrix[i][j] = checkBoardAroundPoint(copiedSlice, { x: j, y: i }).maxLength;
            }
        }

        return resultMatrix;
    }

    updatePositions() {
        if (this.renderer.width < 700 || this.renderer.height < 700) {
            scale = 0.5;
        } else {
            scale = 1;
        }
        this.board.boardContainer.setScale(scale)
        this.modalService.afterResize(scale);
    }
}
